import { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react'
import { fabric } from 'fabric'
import { DrawingType } from '../../types/project'
import { useDrawingStore } from '../../stores/drawingStore'

interface DrawingCanvasProps {
  currentTab: DrawingType
}

export interface CanvasHandle {
  addComponent: (type: string) => void
  setBackgroundImage: (url: string) => void
  getCanvas: () => fabric.Canvas | null
}

const DrawingCanvas = forwardRef<CanvasHandle, DrawingCanvasProps>(({ currentTab }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const fabricCanvasRef = useRef<fabric.Canvas | null>(null)
  const { mode, selectedWireType } = useDrawingStore()
  const [isDrawingLine, setIsDrawingLine] = useState(false)
  const [lineStartPoint, setLineStartPoint] = useState<{ x: number; y: number } | null>(null)
  const [tempLine, setTempLine] = useState<fabric.Line | null>(null)

  useEffect(() => {
    if (!canvasRef.current) return

    // Fabric.js Canvas初期化
    const canvas = new fabric.Canvas(canvasRef.current, {
      width: window.innerWidth - 256,
      height: window.innerHeight - 120,
      backgroundColor: '#f5f5f5',
    })

    fabricCanvasRef.current = canvas

    // グリッド描画
    addGrid(canvas)

    // Delete キーでオブジェクト削除
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const activeObject = canvas.getActiveObject()
        if (activeObject) {
          canvas.remove(activeObject)
          canvas.renderAll()
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)

    // リサイズ対応
    const handleResize = () => {
      canvas.setDimensions({
        width: window.innerWidth - 256,
        height: window.innerHeight - 120,
      })
      canvas.renderAll()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('resize', handleResize)
      canvas.dispose()
    }
  }, [])

  // モード変更時の処理
  useEffect(() => {
    if (!fabricCanvasRef.current) return
    
    const canvas = fabricCanvasRef.current
    
    if (mode === 'select') {
      canvas.isDrawingMode = false
      canvas.selection = true
      setIsDrawingLine(false)
      setLineStartPoint(null)
      if (tempLine) {
        canvas.remove(tempLine)
        setTempLine(null)
      }
    } else if (mode === 'line') {
      canvas.isDrawingMode = false
      canvas.selection = false
    } else {
      canvas.isDrawingMode = false
    }
  }, [mode, tempLine])

  // 線描画のマウスイベント
  useEffect(() => {
    const canvas = fabricCanvasRef.current
    if (!canvas) return

    const handleMouseDown = (e: fabric.IEvent) => {
      if (mode === 'line') {
        const pointer = canvas.getPointer(e.e as MouseEvent)
        
        if (!isDrawingLine) {
          // 線の開始点
          setIsDrawingLine(true)
          setLineStartPoint(pointer)
        } else {
          // 線の終了点 - 確定
          if (lineStartPoint) {
            // 配線情報を入力
            const pipeSize = prompt('配管サイズを入力 (例: CD 22)', 'CD 22') || 'CD 22'
            const cableType = prompt('ケーブル種類を入力 (例: CV 5.5sq-3C)', 'CV 5.5sq-3C') || 'CV 5.5sq-3C'
            const length = Math.sqrt(
              Math.pow(pointer.x - lineStartPoint.x, 2) +
              Math.pow(pointer.y - lineStartPoint.y, 2)
            ) / 20 // 20px = 1m
            
            const color = selectedWireType === 'power' ? '#2563eb' : '#16a34a'
            const line = new fabric.Line(
              [lineStartPoint.x, lineStartPoint.y, pointer.x, pointer.y],
              {
                stroke: color,
                strokeWidth: 3,
                selectable: true,
                hasControls: false,
              }
            )
            
            // カスタムデータを保存
            ;(line as any).wireData = {
              wireType: selectedWireType,
              pipeSize,
              cableType,
              length: parseFloat(length.toFixed(1))
            }
            
            canvas.add(line)
            
            // 一時線を削除
            if (tempLine) {
              canvas.remove(tempLine)
              setTempLine(null)
            }
            
            setIsDrawingLine(false)
            setLineStartPoint(null)
          }
        }
      }
    }

    const handleMouseMove = (e: fabric.IEvent) => {
      if (mode === 'line' && isDrawingLine && lineStartPoint) {
        const pointer = canvas.getPointer(e.e as MouseEvent)
        
        // 一時線を更新
        if (tempLine) {
          canvas.remove(tempLine)
        }
        
        const color = selectedWireType === 'power' ? '#2563eb' : '#16a34a'
        const newTempLine = new fabric.Line(
          [lineStartPoint.x, lineStartPoint.y, pointer.x, pointer.y],
          {
            stroke: color,
            strokeWidth: 2,
            strokeDashArray: [5, 5],
            selectable: false,
            evented: false,
          }
        )
        
        canvas.add(newTempLine)
        setTempLine(newTempLine)
        canvas.renderAll()
      }
    }

    canvas.on('mouse:down', handleMouseDown)
    canvas.on('mouse:move', handleMouseMove)

    return () => {
      canvas.off('mouse:down', handleMouseDown)
      canvas.off('mouse:move', handleMouseMove)
    }
  }, [mode, isDrawingLine, lineStartPoint, selectedWireType, tempLine])

  const addGrid = (canvas: fabric.Canvas) => {
    const gridSize = 20
    const width = canvas.width || 800
    const height = canvas.height || 600

    // 縦線
    for (let i = 0; i < width; i += gridSize) {
      canvas.add(
        new fabric.Line([i, 0, i, height], {
          stroke: '#e0e0e0',
          strokeWidth: 1,
          selectable: false,
          evented: false,
        })
      )
    }

    // 横線
    for (let j = 0; j < height; j += gridSize) {
      canvas.add(
        new fabric.Line([0, j, width, j], {
          stroke: '#e0e0e0',
          strokeWidth: 1,
          selectable: false,
          evented: false,
        })
      )
    }

    canvas.renderAll()
  }

  // 部品追加メソッド
  const addComponent = (type: string) => {
    const canvas = fabricCanvasRef.current
    if (!canvas) return

    let shape: fabric.Object
    const centerX = (canvas.width || 800) / 2
    const centerY = (canvas.height || 600) / 2

    switch (type) {
      case 'EV充電器':
        shape = new fabric.Rect({
          left: centerX - 20,
          top: centerY - 30,
          width: 40,
          height: 60,
          fill: '#3b82f6',
          stroke: '#1e40af',
          strokeWidth: 2,
        })
        break
      
      case '分電盤':
        shape = new fabric.Rect({
          left: centerX - 30,
          top: centerY - 30,
          width: 60,
          height: 60,
          fill: '#f97316',
          stroke: '#c2410c',
          strokeWidth: 2,
        })
        break
      
      case 'ブレーカー':
        shape = new fabric.Rect({
          left: centerX - 15,
          top: centerY - 15,
          width: 30,
          height: 30,
          fill: '#ef4444',
          stroke: '#991b1b',
          strokeWidth: 2,
        })
        break
      
      case 'プルボックス':
        shape = new fabric.Rect({
          left: centerX - 20,
          top: centerY - 20,
          width: 40,
          height: 40,
          fill: '#9ca3af',
          stroke: '#374151',
          strokeWidth: 2,
        })
        break
      
      case '案内板':
        shape = new fabric.Rect({
          left: centerX - 25,
          top: centerY - 35,
          width: 50,
          height: 70,
          fill: '#22c55e',
          stroke: '#15803d',
          strokeWidth: 2,
        })
        break
      
      case '電柱':
        shape = new fabric.Circle({
          left: centerX - 15,
          top: centerY - 15,
          radius: 15,
          fill: '#eab308',
          stroke: '#854d0e',
          strokeWidth: 2,
        })
        break
      
      default:
        return
    }

    // ラベル追加
    const text = new fabric.Text(type, {
      left: shape.left! - 10,
      top: shape.top! - 25,
      fontSize: 12,
      fill: '#374151',
      fontFamily: 'sans-serif',
    })

    const group = new fabric.Group([shape, text], {
      left: shape.left,
      top: shape.top,
    })

    canvas.add(group)
    canvas.setActiveObject(group)
    canvas.renderAll()
  }

  // 背景画像設定メソッド
  const setBackgroundImage = (url: string) => {
    const canvas = fabricCanvasRef.current
    if (!canvas) return

    fabric.Image.fromURL(url, (img: fabric.Image) => {
      if (!canvas.width || !canvas.height) return
      
      // Canvas サイズに合わせて画像をスケール
      const scale = Math.min(
        canvas.width / (img.width || 1),
        canvas.height / (img.height || 1)
      )
      
      img.scale(scale)
      canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas), {
        originX: 'left',
        originY: 'top',
      })
    }, { crossOrigin: 'anonymous' })
  }

  // 外部から呼び出せるようにする
  useImperativeHandle(ref, () => ({
    addComponent,
    setBackgroundImage,
    getCanvas: () => fabricCanvasRef.current,
  }))

  return (
    <div className="flex-1 bg-gray-100 p-4 overflow-hidden">
      <div className="bg-white rounded shadow-sm h-full flex items-center justify-center relative">
        <canvas ref={canvasRef} />
        
        {/* 図面タイトル表示 */}
        <div className="absolute top-4 left-4 bg-white px-3 py-1 rounded shadow text-sm text-gray-600">
          {currentTab === 'layout' && '設置場所見取図'}
          {currentTab === 'plan' && '平面図'}
          {currentTab === 'wiring' && '配線ルート図'}
          {currentTab === 'diagram' && '電気系統図'}
        </div>

        {/* 操作ヒント */}
        {mode === 'line' && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-2 rounded shadow-lg text-sm">
            クリックして線を引いてください。もう一度クリックで確定。
          </div>
        )}
      </div>
    </div>
  )
})

DrawingCanvas.displayName = 'DrawingCanvas'

export default DrawingCanvas
